import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import * as THREE from 'three';
import { Canvas } from '@react-three/fiber';
import { Environment } from '@react-three/drei';
import { GIFEncoder, quantize, applyPalette } from 'gifenc';
import apiService from '../services/apiService';
import { AnimatedPath } from './AnimatedPath';
import { Blob as EthicalAuraBlob } from './blob/Blob';
import { BlobKeepAnimating } from './blob/BlobKeepAnimating';
import { mergeBlobVariantConfig } from './blob/blobVariantConfig';
import {
  isFaceScannerName,
  isProbioticArchitectureName,
  resolveArtefactBlobConfig,
  usesReducedBlobLighting,
} from './blob/resolveArtefactBlobConfig';
import { STUDIO_ENVIRONMENT_HDRI } from '../config/environmentHdri';
import CanvasCapture from './CanvasCapture';
import {
  filterClassificationsForArtefact,
  getColorForCategory,
  getPoleDescription,
  getPoleDisplayName,
  getPolesForArtefact,
  isPoleValidForArtefact,
} from '../config/ethicalDimensions';
import { useLanguage } from '../contexts/LanguageContext';
import { enhanceColorVisibility } from '../utils/blobColorUtils';
import './EthicalDimensionVis.css';

const LANDING_BLOB_ICOSAHEDRON_DETAIL = 40;
const PROBIOTIC_LANDING_BLOB_ICOSAHEDRON_DETAIL = 85;

const MAX_HISTORY = 5;
const MARGIN = 115; // Increased margin to reduce size by ~5%
const DEFAULT_AURA_COLOR = '#d3d3d3';
/** Aura legend: 12px at 1 hit, +2px per extra hit, cap 24px. */
const AURA_LEGEND_LABEL_MIN_PX = 12;
const AURA_LEGEND_LABEL_MAX_PX = 24;
const AURA_LEGEND_LABEL_COUNT_STEP_PX = 2;
const AURA_LEGEND_DESCRIPTION_PX = 10;

function auraLegendFontSizeForCount(count) {
  const n = Math.max(1, Math.round(Number(count) || 1));
  return Math.min(
    AURA_LEGEND_LABEL_MAX_PX,
    AURA_LEGEND_LABEL_MIN_PX + Math.max(0, n - 1) * AURA_LEGEND_LABEL_COUNT_STEP_PX,
  );
}

// Helper function to determine if color is light or dark
const getTextColor = (backgroundColor) => {
  const color = backgroundColor.replace('#', '');
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);
  // Calculate brightness using relative luminance formula
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#1d1d1f' : '#ffffff';
};

const FONT_STACK = "'Inter', sans-serif";
/** Match `index.css` / `.App-header` + `.artefact-name` for export overlay */
const FONT_HEADING_STACK = "'Inter', sans-serif";
const FONT_BODY_STACK = "'Inter', sans-serif";
const BRAND_COLOR_PRIMARY = '#1d1d1f';
const BRAND_COLOR_SECONDARY = '#6e6e73';

/** Frames for aura GIF export (~3s loop); delay per frame (ms). */
const AURA_GIF_FRAME_COUNT = 60;
const AURA_GIF_FRAME_DELAY_MS = 50;

function auraGifDownloadFilename(artefactLabel) {
  const name = String(artefactLabel || 'artefact').trim();
  const safe = name.replace(/[/\\:*?"<>|]/g, '').trim() || 'artefact';
  return `ethical aura for ${safe}.gif`;
}

/**
 * gifenc `applyPalette(rgba, palette, format)` has no `{ dither }` option (arg 3 is e.g. `"rgb565"` only).
 * Use `'none'` for plain nearest-colour; `'bayer'` applies ordered dither in RGB before `applyPalette`.
 */
const AURA_GIF_DITHER = 'bayer'; // 'none' | 'bayer'
/** Bayer strength (try ~2–5); maps to RGB spread before quantization */
const AURA_GIF_BAYER_SCALE = 3;

const BAYER_4X4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
];

/** In-place RGB only; alpha unchanged. `getImageData` is a copy — safe to mutate. */
function applyBayerDitherRgbInPlace(rgba, width, height, bayerScale) {
  const strength = 8 * bayerScale;
  for (let y = 0; y < height; y += 1) {
    const y3 = y & 3;
    for (let x = 0; x < width; x += 1) {
      const t = (BAYER_4X4[y3][x & 3] + 0.5) / 16 - 0.5;
      const d = t * strength;
      const i = (y * width + x) * 4;
      rgba[i] = Math.max(0, Math.min(255, Math.round(rgba[i] + d)));
      rgba[i + 1] = Math.max(0, Math.min(255, Math.round(rgba[i + 1] + d)));
      rgba[i + 2] = Math.max(0, Math.min(255, Math.round(rgba[i + 2] + d)));
    }
  }
}

function waitAnimationFrames(count) {
  return new Promise((resolve) => {
    let n = 0;
    function tick() {
      n += 1;
      if (n >= count) resolve();
      else requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  });
}

function dataUrlToCanvas(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.width;
      c.height = img.height;
      c.getContext('2d').drawImage(img, 0, 0);
      resolve(c);
    };
    img.onerror = () => reject(new Error('Failed to decode frame'));
    img.src = dataUrl;
  });
}

/**
 * Encode RGBA frames (same dimensions) as a looping GIF.
 */
function encodeGifFromCanvases(canvases, delayMs) {
  if (!canvases.length) throw new Error('No frames');
  const w = canvases[0].width;
  const h = canvases[0].height;
  const mega = new Uint8Array(w * h * 4 * canvases.length);
  let offset = 0;
  for (let i = 0; i < canvases.length; i += 1) {
    const c = canvases[i];
    if (c.width !== w || c.height !== h) {
      throw new Error('GIF frames must share dimensions');
    }
    const d = c.getContext('2d').getImageData(0, 0, w, h).data;
    mega.set(d, offset);
    offset += d.length;
  }
  const palette = quantize(mega, 256);
  const gif = GIFEncoder();
  canvases.forEach((canvas, i) => {
    const rgba = canvas.getContext('2d').getImageData(0, 0, w, h).data;
    if (AURA_GIF_DITHER === 'bayer') {
      applyBayerDitherRgbInPlace(rgba, w, h, AURA_GIF_BAYER_SCALE);
    }
    const index = applyPalette(rgba, palette);
    if (i === 0) {
      gif.writeFrame(index, w, h, {
        palette,
        delay: delayMs,
        repeat: 0,
      });
    } else {
      gif.writeFrame(index, w, h, { delay: delayMs });
    }
  });
  gif.finish();
  return gif.bytes();
}

/**
 * Build a raster snapshot: same canvas size as the blob capture, with legend painted **bottom-left** like
 * `.category-labels-overlay` / `.category-labels-list`. Typography is **scaled up** vs literal CSS px
 * so legend stays readable relative to large WebGL captures (same proportions as on-screen aura).
 * Optional `branding` draws app title + artefact name **top-left** like `.App-header` / `.artefact-name`.
 */
function buildAuraDownloadImage(blobDataUrl, keywords, branding = {}) {
  const {
    headerTitle = '',
    artefactLabel = '',
    locale = 'en',
    legendHeader = 'Your Ethical Aura consists of:',
    legendEmpty = 'No information yet',
  } = branding;
  const titleText = legendHeader;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const cw = img.width;
        const ch = img.height;
        const canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas unsupported'));
          return;
        }

        ctx.drawImage(img, 0, 0);

        const items = Array.isArray(keywords) ? keywords : [];

        // Scale legend so labels/header match perceived UI size on large exported canvases (~580px CSS reference).
        const refCssPx = 580;
        const legendScale = Math.max(
          1.5,
          Math.min(Math.min(cw, ch) / refCssPx, 3.8)
        );

        const padX = 24 * legendScale;
        const padY = 16 * legendScale;

        const titleUpper = headerTitle ? String(headerTitle).toLocaleUpperCase(locale) : '';
        const artefactStr = artefactLabel ? String(artefactLabel) : '';
        if (titleUpper || artefactStr) {
          const titleFontSize = 16 * legendScale;
          const artefactFontSize = 12 * legendScale;
          const titleToArtefactGap = 4 * legendScale;
          const brandPad = 10 * legendScale;
          const maxBrandW = cw - padX * 2;
          const maxTextW = Math.max(0, maxBrandW - 2 * brandPad);

          ctx.textBaseline = 'top';
          let titleDraw = titleUpper;
          ctx.font = `500 ${titleFontSize}px ${FONT_HEADING_STACK}`;
          if (titleUpper && maxTextW > 0) {
            while (titleDraw.length > 1 && ctx.measureText(titleDraw).width > maxTextW) {
              titleDraw = `${titleDraw.slice(0, -4)}…`;
            }
          }
          let artefactDraw = artefactStr;
          ctx.font = `400 ${artefactFontSize}px ${FONT_BODY_STACK}`;
          if (artefactStr && maxTextW > 0) {
            while (artefactDraw.length > 1 && ctx.measureText(artefactDraw).width > maxTextW) {
              artefactDraw = `${artefactDraw.slice(0, -4)}…`;
            }
          }

          ctx.font = `500 ${titleFontSize}px ${FONT_HEADING_STACK}`;
          const wTitle = titleDraw ? ctx.measureText(titleDraw).width : 0;
          ctx.font = `400 ${artefactFontSize}px ${FONT_BODY_STACK}`;
          const wArtefact = artefactDraw ? ctx.measureText(artefactDraw).width : 0;

          const titleBlockH = titleDraw ? titleFontSize * 1.25 : 0;
          const gapBetween = titleDraw && artefactDraw ? titleToArtefactGap : 0;
          const artefactBlockH = artefactDraw ? artefactFontSize * 1.25 : 0;
          const innerH = titleBlockH + gapBetween + artefactBlockH;
          const innerW = Math.min(maxBrandW, Math.max(wTitle, wArtefact, 1) + 2 * brandPad);

          const boxX = padX - brandPad;
          const boxY = padY - brandPad;
          const boxW = innerW;
          const boxH = innerH + 2 * brandPad;
          const r = 8 * legendScale;
          ctx.save();
          ctx.fillStyle = 'rgba(255, 255, 255, 0.88)';
          if (typeof ctx.roundRect === 'function') {
            ctx.beginPath();
            ctx.roundRect(boxX, boxY, boxW, boxH, r);
            ctx.fill();
          } else {
            ctx.fillRect(boxX, boxY, boxW, boxH);
          }
          ctx.restore();

          let yBrand = boxY + brandPad;
          if (titleDraw) {
            ctx.fillStyle = BRAND_COLOR_PRIMARY;
            ctx.font = `500 ${titleFontSize}px ${FONT_HEADING_STACK}`;
            ctx.textBaseline = 'top';
            ctx.fillText(titleDraw, boxX + brandPad, yBrand);
            yBrand += titleBlockH + gapBetween;
          }
          if (artefactDraw) {
            ctx.fillStyle = BRAND_COLOR_SECONDARY;
            ctx.font = `400 ${artefactFontSize}px ${FONT_BODY_STACK}`;
            ctx.fillText(artefactDraw, boxX + brandPad, yBrand);
          }
        }
        const headerFontSize = 12 * legendScale;
        const headerMarginBottom = 12 * legendScale;
        const itemGap = Math.max(2, 2 * legendScale);
        const dotSize = 15 * legendScale;
        const labelGap = 8 * legendScale;
        const minLabelPx = Math.max(10, 10 * legendScale);

        const measureHeaderH = () => headerFontSize * 1.25;

        const measureRowH = (cat) => {
          const baseFs = Math.max(8, Math.round(Number(cat.fontSize) || 16));
          const fs = Math.max(minLabelPx, Math.round(baseFs * legendScale));
          return Math.max(dotSize, fs * 1.15);
        };

        let legendBodyH = 0;
        if (items.length === 0) {
          legendBodyH = headerFontSize * 1.25;
        } else {
          items.forEach((cat, i) => {
            legendBodyH += measureRowH(cat);
            if (i < items.length - 1) legendBodyH += itemGap;
          });
        }

        const headerBlockH = measureHeaderH() + headerMarginBottom;
        const totalLegendH = headerBlockH + legendBodyH;

        let yTop = ch - padY - totalLegendH;

        ctx.textBaseline = 'top';

        ctx.fillStyle = '#1d1d1f';
        ctx.font = `500 ${headerFontSize}px ${FONT_STACK}`;
        ctx.fillText(titleText, padX, yTop);

        yTop += measureHeaderH() + headerMarginBottom;

        const maxLabelW = cw - padX - dotSize - labelGap - padX;

        if (items.length === 0) {
          ctx.fillStyle = '#86868b';
          ctx.font = `italic 400 ${headerFontSize}px ${FONT_STACK}`;
          ctx.fillText(legendEmpty, padX, yTop);
        } else {
          items.forEach((cat, index) => {
            const rowH = measureRowH(cat);
            const cy = yTop + rowH / 2;
            const cx = padX + dotSize / 2;

            ctx.fillStyle = cat.color || '#AFEEEE';
            ctx.beginPath();
            ctx.arc(cx, cy, dotSize / 2, 0, Math.PI * 2);
            ctx.fill();

            const baseFs = Math.max(8, Math.round(Number(cat.fontSize) || 16));
            let fs = Math.max(minLabelPx, Math.round(baseFs * legendScale));
            const label = String(cat.displayName || cat.name || '');
            ctx.font = `300 ${fs}px ${FONT_STACK}`;
            while (fs > minLabelPx && ctx.measureText(label).width > maxLabelW) {
              fs -= 1;
              ctx.font = `300 ${fs}px ${FONT_STACK}`;
            }
            ctx.fillStyle = '#000000';
            ctx.textBaseline = 'middle';
            ctx.fillText(label, padX + dotSize + labelGap, cy);

            yTop += rowH;
            if (index < items.length - 1) yTop += itemGap;
          });
        }

        resolve(canvas.toDataURL('image/png')); // intermediate; GIF encoder consumes pixels
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Failed to load blob snapshot'));
    img.src = blobDataUrl;
  });
}

const EthicalDimensionVis = React.forwardRef(({
  sessionId,
  userMessageCount = 0,
  onBlobClick,
  showLegend = false,
  immediateClassifications = [],
  localCategories = {},
  variant = 'default',
  artefactName = '',
  downloadHeaderTitle = '',
  downloadArtefactLabel = '',
  enableCapture = false,
}, ref) => {
  const { t, language } = useLanguage();
  const [history, setHistory] = useState([]);

  const artefactCategories = React.useMemo(
    () => getPolesForArtefact(artefactName),
    [artefactName]
  );

  const filteredImmediateClassifications = React.useMemo(
    () => filterClassificationsForArtefact(immediateClassifications, artefactName),
    [immediateClassifications, artefactName]
  );
  // loading removed
  const containerRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const captureFunctionRef = useRef(null);
  const categoryFrequenciesRef = useRef([]);
  const downloadBrandingRef = useRef({ headerTitle: '', artefactLabel: '' });
  const auraLegendCopyRef = useRef({ legendHeader: '', legendEmpty: '' });
  auraLegendCopyRef.current = {
    legendHeader: t('aura.legendHeader'),
    legendEmpty: t('aura.noInformationYet'),
  };
  const [dimensions, setDimensions] = useState({ width: 600, height: 600 });

  useEffect(() => {
    downloadBrandingRef.current = {
      headerTitle: downloadHeaderTitle || '',
      artefactLabel: downloadArtefactLabel || '',
    };
  }, [downloadHeaderTitle, downloadArtefactLabel]);

  // Listen for download event from footer button
  useEffect(() => {
    const handleDownload = async () => {
      try {
        if (!captureFunctionRef.current) {
          console.error('Capture function not available');
          return;
        }
        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready;
        }
        const keywords = categoryFrequenciesRef.current || [];
        const locale =
          typeof document !== 'undefined' && document.documentElement.lang
            ? document.documentElement.lang
            : 'en';
        const branding = {
          ...downloadBrandingRef.current,
          ...auraLegendCopyRef.current,
          locale,
        };
        const frameUrls = [];
        for (let f = 0; f < AURA_GIF_FRAME_COUNT; f += 1) {
          await waitAnimationFrames(2);
          const blobOnly = captureFunctionRef.current();
          if (!blobOnly) {
            console.error('Failed to capture canvas');
            return;
          }
          const dataURL = await buildAuraDownloadImage(blobOnly, keywords, branding);
          frameUrls.push(dataURL);
        }
        const canvases = [];
        for (let i = 0; i < frameUrls.length; i += 1) {
          canvases.push(await dataUrlToCanvas(frameUrls[i]));
        }
        const bytes = encodeGifFromCanvases(canvases, AURA_GIF_FRAME_DELAY_MS);
        const blob = new Blob([bytes], { type: 'image/gif' });
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = auraGifDownloadFilename(downloadBrandingRef.current.artefactLabel);
        link.href = objectUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error('Error capturing visualization:', error);
      } finally {
        window.dispatchEvent(new CustomEvent('downloadBlobComplete'));
      }
    };

    window.addEventListener('downloadBlob', handleDownload);
    return () => {
      window.removeEventListener('downloadBlob', handleDownload);
    };
  }, []);

  // Update dimensions on resize
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        // Use the smaller dimension for square aspect ratio, but ensure it doesn't exceed parent
        // Ensure we respect the actual available height
        const availableHeight = height || 600;
        const availableWidth = width || 600;
        const size = Math.max(300, Math.min(availableWidth, availableHeight)); 
        setDimensions({ width: size, height: size });
      }
    };
    
    // Initial size
    updateSize();
    
    // Use ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
      resizeObserver.disconnect();
    };
  }, []);

  // Wrap in useCallback to avoid infinite loop when adding to useEffect deps
  const fetchHistory = React.useCallback(async () => {
    if (!sessionId) return;
    try {
      const data = await apiService.getInteractionHistory(sessionId, MAX_HISTORY);
      if (data.success && data.classifications) {
         // data.classifications is a flat list. Group by message_id
         const grouped = [];
         let currentGroup = null;
         
         // Backend returns sorted by newest first
         data.classifications.forEach(item => {
             if (!isPoleValidForArtefact(item.category, artefactName)) return;
             if (!currentGroup || currentGroup.message_id !== item.message_id) {
                 if (currentGroup) grouped.push(currentGroup);
                 currentGroup = {
                     message_id: item.message_id,
                     timestamp: item.created_at,
                     categories: []
                 };
             }
             currentGroup.categories.push(item);
         });
         if (currentGroup) grouped.push(currentGroup);

         // Assign a stable random color for each interaction
         grouped.forEach(group => {
            if (group.categories.length > 0) {
                // Use message_id char codes to pick a pseudo-random index
                const charCodeSum = group.message_id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                const randomIndex = charCodeSum % group.categories.length;
                const randomCategory = group.categories[randomIndex].category;
                group.displayColor = getColorForCategory(randomCategory);
            } else {
                group.displayColor = '#DDA0DD';
            }
         });
         
         setHistory(grouped);
      }
    } catch (e) {
      console.error("Failed to fetch history", e);
    } 
  }, [sessionId, artefactName]);

  // Expose container ref and refresh method to parent (after fetchHistory is defined)
  React.useImperativeHandle(ref, () => ({
    current: containerRef.current,
    refreshHistory: fetchHistory
  }), [fetchHistory]);

  useEffect(() => {
    fetchHistory();
  }, [sessionId, fetchHistory]);


  // D3 Scales and Path Generation
  const outerRadius = (dimensions.width / 2) - MARGIN;
  
  const xScale = d3.scaleBand()
    .domain(artefactCategories)
    .range([0, 2 * Math.PI])
    .align(0);

  const yScale = d3.scaleLinear()
    .domain([0, 0.5]) 
    .range([0, outerRadius]);

  const lineGenerator = d3.lineRadial()
    .angle(d => xScale(d.category))
    .radius(d => yScale(d.value || 0))
    .curve(d3.curveLinearClosed);


  // Helper function to prepare blob data from interaction
  // Uses ALL categories with their actual frequencies/scores
  const prepareBlobData = (interaction) => {
    if (!interaction.categories || interaction.categories.length === 0) {
      // Return 5 elements with default color
      return { 
        colors: ['#DDA0DD', '#DDA0DD', '#DDA0DD', '#DDA0DD', '#DDA0DD'], 
        weights: [1.0, 0, 0, 0, 0] 
      };
    }

    // Use ALL categories with their actual scores (frequencies)
    const allCategories = [...interaction.categories];
    const colors = allCategories
      .map((cat) => getColorForCategory(cat.category))
      .filter(Boolean);
    const scores = allCategories.map(cat => cat.score || 0.4);
    
    // Normalize weights to sum to 1.0 (reflecting actual frequencies)
    const totalScore = scores.reduce((sum, score) => sum + score, 0);
    const weights = totalScore > 0 
      ? scores.map(score => score / totalScore)
      : scores.map(() => 1.0 / scores.length);

    // If we have more than 5 categories, combine the smallest ones into the 5th slot
    // This preserves the frequency distribution while fitting shader constraints
    if (colors.length > 5) {
      // Sort by weight (descending) to keep most significant categories
      const indexed = colors.map((color, i) => ({ color, weight: weights[i] }))
        .sort((a, b) => b.weight - a.weight);
      
      // Take top 4 categories
      const top4 = indexed.slice(0, 4);
      
      // Combine remaining categories into 5th slot (weighted average color)
      const remaining = indexed.slice(4);
      const remainingTotalWeight = remaining.reduce((sum, item) => sum + item.weight, 0);
      
      if (remainingTotalWeight > 0) {
        // Calculate weighted average color for remaining categories
        const remainingColors = remaining.map(item => {
          const c = new THREE.Color(item.color);
          return { r: c.r * item.weight, g: c.g * item.weight, b: c.b * item.weight, weight: item.weight };
        });
        const avgColor = remainingColors.reduce(
          (acc, c) => ({ r: acc.r + c.r, g: acc.g + c.g, b: acc.b + c.b }),
          { r: 0, g: 0, b: 0 }
        );
        const finalColor = new THREE.Color(
          avgColor.r / remainingTotalWeight,
          avgColor.g / remainingTotalWeight,
          avgColor.b / remainingTotalWeight
        );
        
        // Convert to hex string with # prefix
        const hexColor = '#' + finalColor.getHexString();
        
        return {
          colors: [...top4.map(item => item.color), hexColor],
          weights: [...top4.map(item => item.weight), remainingTotalWeight]
        };
      } else {
        // Pad with default if no remaining weight
        return {
          colors: [...top4.map(item => item.color), '#DDA0DD'],
          weights: [...top4.map(item => item.weight), 0]
        };
      }
    }

    // Pad to exactly 5 elements if needed
    while (colors.length < 5) {
      colors.push('#DDA0DD');
      weights.push(0);
    }


    // Ensure we have exactly 5 elements
    return { 
      colors: colors.slice(0, 5), 
      weights: weights.slice(0, 5) 
    };
  };

  // Render Function for each history item
  const renderLayer = (interaction, index) => {
    // Index 0 is newest.
    
    // Create data object ONLY for active categories to form a shape connecting them directly (Triangle)
    const interactionData = artefactCategories
      .filter(cat => interaction.categories.some(c => c.category === cat))
      .map(cat => {
        const found = interaction.categories.find(c => c.category === cat);
        const rawValue = found ? (found.score || 0.4) : 0.4;
        // Clamp value to 0.5 (max radius) to prevent points going outside the chart
        const clampedValue = Math.min(rawValue, 0.5);
        return { 
          category: cat, 
          value: clampedValue
        };
      });

    if (interactionData.length < 3) {
        // Fallback or handle small shapes
    }

    const path = lineGenerator(interactionData);
    
    // Use the assigned random color
    const color = interaction.displayColor;

    // "Active Layer" (Index 0) gets a FIXED KEY so it morphs from the previous state.
    // "History Layers" (Index > 0) get their own keys to stay distinct in background.
    const isNewest = index === 0;
    const key = isNewest ? 'active-layer-morph' : interaction.message_id;

    return (
        <AnimatedPath
            key={key}
            path={path}
            color={color}
            opacity={isNewest ? 0.4 : 0.1} 
            blur={isNewest ? 0 : (index * 2)} // Blur older interactions
            isActive={isNewest} // Pass flag for slower config if needed
        />
    );
  };

  const axisConfig = artefactCategories.map(c => ({ name: c, max: 1 }));

  // Compute unique categories for legend
  const uniqueCategories = React.useMemo(() => {
    const allCategoryNames = new Set();
    history.forEach((interaction) => {
      if (interaction && interaction.categories) {
        interaction.categories
          .filter((cat) => (cat.score || 0) > 0 && isPoleValidForArtefact(cat.category, artefactName))
          .forEach((cat) => allCategoryNames.add(cat.category));
      }
    });
    return Array.from(allCategoryNames)
      .map((categoryName) => {
        const color = getColorForCategory(categoryName);
        if (!color) return null;
        return {
          name: categoryName,
          color,
          textColor: getTextColor(color),
        };
      })
      .filter(Boolean);
  }, [history, artefactName]);

  const hasLiveAuraCounts = React.useMemo(() => {
    const hasLocal = Object.entries(localCategories || {}).some(([category, count]) => {
      const n = Number(count);
      return isPoleValidForArtefact(category, artefactName) && Number.isFinite(n) && n > 0;
    });
    return hasLocal || filteredImmediateClassifications.length > 0;
  }, [localCategories, filteredImmediateClassifications, artefactName]);

  // Compute category frequencies for label sizing
  const categoryFrequencies = React.useMemo(() => {
    const frequencyMap = {};
    const descriptionByCategory = {};

    const noteDescription = (category, description) => {
      if (!category || descriptionByCategory[category]) return;
      const fromConfig = getPoleDescription(category, language);
      const text = fromConfig || String(description || '').trim();
      if (text) descriptionByCategory[category] = text;
    };

    const localCountEntries = Object.entries(localCategories || {}).filter(([category, count]) => {
      const n = Number(count);
      return isPoleValidForArtefact(category, artefactName) && Number.isFinite(n) && n > 0;
    });

    if (localCountEntries.length > 0) {
      // Primary: App-maintained per-category counts (authoritative during live chat).
      localCountEntries.forEach(([category, count]) => {
        frequencyMap[category] = Math.round(Number(count));
      });
      filteredImmediateClassifications.forEach((classification) => {
        if (classification?.category) {
          noteDescription(classification.category, classification.description);
        }
      });
    } else if (filteredImmediateClassifications.length > 0) {
      filteredImmediateClassifications.forEach((classification) => {
        if (classification && classification.category) {
          frequencyMap[classification.category] = (frequencyMap[classification.category] || 0) + 1;
          noteDescription(classification.category, classification.description);
        }
      });
    } else {
      history.forEach((interaction) => {
        if (interaction && interaction.categories) {
          interaction.categories.forEach((cat) => {
            const score = cat.score || 0;
            if (score > 0 && isPoleValidForArtefact(cat.category, artefactName)) {
              frequencyMap[cat.category] = (frequencyMap[cat.category] || 0) + 1;
              noteDescription(cat.category, cat.description);
            }
          });
        }
      });
    }

    // Convert to array and sort by frequency (descending)
    const categories = Object.entries(frequencyMap)
      .map(([name, frequency]) => {
        const color = getColorForCategory(name);
        if (!color) return null;
        const description =
          getPoleDescription(name, language) || descriptionByCategory[name] || '';
        const displayName = getPoleDisplayName(name, language) || name;
        return { name, displayName, frequency, color, description };
      })
      .filter(Boolean)
      .sort((a, b) => b.frequency - a.frequency);

    if (categories.length === 0) return [];

    return categories.map((cat) => ({
      ...cat,
      fontSize: auraLegendFontSizeForCount(cat.frequency),
    }));
  }, [
    hasLiveAuraCounts ? null : history,
    filteredImmediateClassifications,
    localCategories,
    artefactName,
    language,
    hasLiveAuraCounts,
  ]);

  categoryFrequenciesRef.current = categoryFrequencies;

  // Handle blob click - call parent handler which controls showLegend
  const handleBlobClick = React.useCallback(() => {
    if (onBlobClick) onBlobClick();
  }, [onBlobClick]);

  // Calculate blob positions in a grid
  const getBlobPosition = (index, total) => {
    if (total === 1) return [0, 0, 0];
    
    const cols = Math.ceil(Math.sqrt(total));
    const rows = Math.ceil(total / cols);
    const col = index % cols;
    const row = Math.floor(index / cols);
    
    const spacing = 3;
    const startX = -(cols - 1) * spacing / 2;
    const startY = -(rows - 1) * spacing / 2;
    
    return [startX + col * spacing, startY + row * spacing, 0];
  };

  // Compute blended gradient colors for the new blob shader
  const gradientColors = React.useMemo(() => {
    const scoreByCategory = {};

    if (filteredImmediateClassifications.length > 0) {
      filteredImmediateClassifications.forEach((classification) => {
        if (!classification || !classification.category) return;
        const score = classification.score || 1.0;
        scoreByCategory[classification.category] = (scoreByCategory[classification.category] || 0) + score;
      });
    } else {
      history.forEach((interaction) => {
        if (!interaction || !interaction.categories) return;
        interaction.categories.forEach((cat) => {
          const score = cat.score || 0;
          if (score > 0 && isPoleValidForArtefact(cat.category, artefactName)) {
            scoreByCategory[cat.category] = (scoreByCategory[cat.category] || 0) + score;
          }
        });
      });
    }

    const topColors = Object.entries(scoreByCategory)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([category]) => {
        const hex = getColorForCategory(category);
        return hex ? enhanceColorVisibility(hex) : null;
      })
      .filter(Boolean);

    while (topColors.length < 3) {
      topColors.push(DEFAULT_AURA_COLOR);
    }

    return {
      color1: topColors[0],
      color2: topColors[1],
      color3: topColors[2],
    };
  }, [history, filteredImmediateClassifications, artefactName]);

  const artefactBlobConfig = React.useMemo(() => resolveArtefactBlobConfig(artefactName), [artefactName]);

  const blobConfig = React.useMemo(
    () => mergeBlobVariantConfig(artefactBlobConfig, variant),
    [artefactBlobConfig, variant]
  );
  const reducedLightingBlob = usesReducedBlobLighting(artefactName);
  const faceScannerBlob = isFaceScannerName(artefactName);
  const probioticBlob = isProbioticArchitectureName(artefactName);
  const renderedBlobConfig = reducedLightingBlob
    ? { ...blobConfig, envMapIntensity: faceScannerBlob ? 0.28 : 0.45 }
    : blobConfig;

  return (
    <div className="ethical-dimension-vis" ref={containerRef} id="blob-visualization-container">
      {/* 3D Blob Visualization */}
      {(history.length > 0 || sessionId) ? (
        <div ref={canvasContainerRef} style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', position: 'relative', background: 'transparent' }}>
          <Canvas 
            camera={{ position: [0, 0, 8], fov: 50 }}
            dpr={probioticBlob ? [1, 1.85] : [1, 1.35]}
            frameloop="always"
            resize={{ scroll: false, debounce: { resize: 0, scroll: 0 } }}
            gl={{
              antialias: true,
              alpha: true,
              preserveDrawingBuffer: enableCapture,
              powerPreference: 'high-performance',
            }}
            style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}
          >
            <BlobKeepAnimating />
            {enableCapture && <CanvasCapture onCaptureReady={(fn) => { captureFunctionRef.current = fn; }} />}
            <ambientLight intensity={faceScannerBlob ? 0.22 : reducedLightingBlob ? 0.34 : 0.6} />
            <directionalLight position={[5, 5, 5]} intensity={faceScannerBlob ? 0.28 : reducedLightingBlob ? 0.42 : 0.8} />
            <pointLight position={[-5, -5, 5]} intensity={faceScannerBlob ? 0.12 : reducedLightingBlob ? 0.2 : 0.4} />
            <Environment files={STUDIO_ENVIRONMENT_HDRI} environmentIntensity={faceScannerBlob ? 0.24 : reducedLightingBlob ? 0.38 : 0.82} />
            <group position={[0, 0, 0]} scale={[1.7, 1.7, 1.7]}>
              <EthicalAuraBlob 
                key={`blob-${filteredImmediateClassifications.length}-${history.length}-${filteredImmediateClassifications.map(c => c?.category).join('-')}-${variant}-${gradientColors.color1}-${gradientColors.color2}-${gradientColors.color3}`}
                gradientColor1={gradientColors.color1}
                gradientColor2={gradientColors.color2}
                gradientColor3={gradientColors.color3}
                config={renderedBlobConfig}
                onClick={handleBlobClick}
                icosahedronDetail={
                  probioticBlob
                    ? PROBIOTIC_LANDING_BLOB_ICOSAHEDRON_DETAIL
                    : LANDING_BLOB_ICOSAHEDRON_DETAIL
                }
              />
            </group>
          </Canvas>
        </div>
      ) : (
        <div style={{ width: dimensions.width, height: dimensions.height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#86868b' }}>
          No data available
        </div>
      )}
      
      {/* Category Labels Overlay - positioned over ethical-dimension-vis */}
      {showLegend && (
        <div className="category-labels-overlay">
          <div className="category-labels-list">
            <div className="category-labels-header">{t('aura.legendHeader')}</div>
            {categoryFrequencies.length > 0 ? (
              categoryFrequencies.map((category, index) => (
                <div
                  key={category.name}
                  className="category-label-item"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <span
                    className="category-label-indicator"
                    style={{ backgroundColor: category.color }}
                  />
                  <span className="category-label-text">
                    <span
                      className="category-label-name"
                      style={{ fontSize: `${category.fontSize}px` }}
                    >
                      {category.displayName || category.name}
                    </span>
                    {category.description ? (
                      <span className="category-label-description">
                        {category.description}
                      </span>
                    ) : null}
                  </span>
                </div>
              ))
            ) : (
              <span className="category-label-item category-label-empty" style={{ fontSize: '16px', color: '#86868b' }}>
                {t('aura.noInformationYet')}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

EthicalDimensionVis.displayName = 'EthicalDimensionVis';

export default EthicalDimensionVis;
